import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { formatRateFraction } from "@bw/shared/types";
import { isApiError } from "@bw/shared/api";
import {
  acceptContractTerms,
  withdrawContractTerms,
  type ContractTerms,
  type Org,
} from "@/libs/onboardingApi";
import { useOwnerContract } from "../../contract/useOwnerContract";
import { OwnerFeeTerms } from "../../contract/OwnerFeeTerms";
import { OwnerContractHistory } from "../../contract/OwnerContractHistory";
import { ExactAgreementDocument } from "../../contract/ExactAgreementDocument";

const LOCKED_ORG_STATUSES = new Set(["approved", "active", "suspended"]);

function formatEffectiveDate(value: string | null | undefined): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "—";
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value)
    return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** Exact current agreement plus its version-owned economics and acceptance history. */
export function ContractTermsPanel({
  org,
  orgId,
}: {
  org: Org;
  orgId: string;
  terms: ContractTerms | null;
  ownerName: string;
}) {
  const queryClient = useQueryClient();
  const safe = useOwnerContract(orgId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const acceptanceKey = useRef(globalThis.crypto.randomUUID());
  const refresh = async () => {
    await Promise.all([
      safe.refresh(),
      queryClient.invalidateQueries({ queryKey: ["org", "me"] }),
      queryClient.invalidateQueries({
        queryKey: ["org", orgId, "onboarding-state"],
      }),
    ]);
  };
  const accept = useMutation({
    mutationFn: (input: { versionId: string; documentSha256: string }) =>
      acceptContractTerms(orgId, {
        expected_version_id: input.versionId,
        expected_document_sha256: input.documentSha256,
      }, acceptanceKey.current),
    onSuccess: async () => {
      await refresh();
      acceptanceKey.current = globalThis.crypto.randomUUID();
      setConfirmOpen(false);
    },
  });
  const withdraw = useMutation({
    mutationFn: () => withdrawContractTerms(orgId),
    onSuccess: refresh,
  });
  const query = safe.termsQuery;
  if (query.isLoading)
    return (
      <Typography role="status">Loading current economic terms…</Typography>
    );
  if (query.isError && isApiError(query.error) && query.error.status === 404)
    return (
      <Alert severity="info">
        <Typography variant="subtitle2">Awaiting commercial terms</Typography>
        Your commercial terms aren’t available yet. They appear here as soon as they take effect, and you can review and accept them then.
        <Button size="small" disabled={query.isFetching} onClick={() => void safe.refresh()}>
          Check for terms
        </Button>
      </Alert>
    );
  if (query.isError)
    return (
      <Alert severity="warning">
        Current Owner terms unavailable.{" "}
        <Button size="small" onClick={() => void safe.refresh()}>
          Retry
        </Button>
      </Alert>
    );
  const terms = query.data;
  if (!terms)
    return <Alert severity="info">Economic terms are not available yet.</Alert>;
  const canAccept =
    terms.requires_reacceptance === true &&
    terms.version_id != null &&
    terms.document != null &&
    formatRateFraction(terms.commission_rate) !== "—";
  const canWithdraw =
    terms.accepted_at != null && !LOCKED_ORG_STATUSES.has(org.status);
  const pending = accept.isPending || withdraw.isPending;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="body2">
          Current economic terms
          {terms.version_number ? ` — version ${terms.version_number}` : ""}.
        </Typography>
        <Typography variant="body2">
          Effective date: {formatEffectiveDate(terms.effective_date)}
        </Typography>
        {!terms.service_fee_terms?.length ? (
          <Typography variant="body2">
            Commission:{" "}
            {formatRateFraction(terms.commission_rate, 2).replace(
              /\.?0+%$/,
              "%",
            )}
          </Typography>
        ) : null}
        <OwnerFeeTerms terms={terms} />
        <ExactAgreementDocument
          document={terms.document}
          unavailableCopy="The platform has not published an exact reviewed agreement for this economic version. It cannot be accepted as a legal agreement."
        />
        <Button size="small" variant="outlined" component={Link} to="/commercial/offers">Review module offers</Button>
        {terms.accepted_at ? (
          <Typography variant="body2">
            Economic terms accepted on{" "}
            {new Date(terms.accepted_at).toLocaleString()}.
          </Typography>
        ) : null}
        {terms.upcoming_version_number ? (
          <Typography variant="body2" color="text.secondary">
            Upcoming version {terms.upcoming_version_number} — for information
            only.
          </Typography>
        ) : null}
        {accept.isError || withdraw.isError ? (
          <Alert severity="error">
            Could not update economic acceptance. Refresh and try again.
          </Alert>
        ) : null}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {canAccept ? (
            <Button
              size="small"
              variant="contained"
              disabled={pending}
              onClick={() => setConfirmOpen(true)}
            >
              Accept current agreement terms
            </Button>
          ) : null}
          {canWithdraw ? (
            <Button
              size="small"
              variant="outlined"
              disabled={pending}
              onClick={() => withdraw.mutate()}
            >
              Withdraw economic acceptance
            </Button>
          ) : null}
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? "Hide contract history" : "View contract history"}
          </Button>
        </Stack>
        {showHistory ? (
          safe.versionsQuery.isError || safe.acceptancesQuery.isError ? (
            <Alert severity="warning">Contract history unavailable.</Alert>
          ) : safe.versionsQuery.isPending ||
            safe.acceptancesQuery.isPending ? (
            <Typography role="status">Loading contract history…</Typography>
          ) : (
            <OwnerContractHistory
              versions={safe.versionsQuery.data ?? []}
              acceptances={safe.acceptancesQuery.data ?? []}
            />
          )
        ) : null}
      </Stack>
      <Dialog
        open={confirmOpen}
        onClose={() => !pending && setConfirmOpen(false)}
        slotProps={{
          paper: {
            role: "alertdialog",
            "aria-labelledby": "economic-confirm-title",
          },
        }}
      >
        <DialogTitle id="economic-confirm-title">
          Accept current agreement terms?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This records your acceptance of version {terms.version_number} and
            its exact SHA-256 document copy. Future publications cannot rewrite
            this accepted copy.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            disabled={pending}
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={pending || !canAccept}
            onClick={() => {
              if (terms.version_id && terms.document) accept.mutate({
                versionId: terms.version_id,
                documentSha256: terms.document.content_sha256,
              });
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
